/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";
import PlaySolidIcon from "@vector-im/compound-design-tokens/assets/web/icons/play-solid";

import styles from "./AgendaStrip.module.css";
import { AgendaProvider, useAgenda, type AgendaState } from "./AgendaContext";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";

/** С какого остатка строка начинает мигать. */
const WARN_SEC = 60;

/** Остаток «5:04»; для перебора знак не нужен — о нём говорит подпись. */
function formatLeft(seconds: number): string {
  const whole = Math.abs(Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

/** Elapsed seconds from the shared start timestamp; null before speaking starts. */
function useElapsed(state: AgendaState | null): number | null {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!state?.speaking || !state.startedAt) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 1000);
    return (): void => window.clearInterval(timer);
  }, [state?.speaking, state?.startedAt]);

  if (!state?.startedAt || (!state.speaking && !state.pausedAt)) return null;
  return Math.max(0, ((state.pausedAt ?? Date.now()) - state.startedAt) / 1000);
}

/**
 * Значок «следующий» — треугольник с палочкой, как на перемотке вперёд
 * (CTO 07.09.2026).
 */
const NextIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M3 3.5v9a.5.5 0 0 0 .77.42l6.5-4.5a.5.5 0 0 0 0-.84l-6.5-4.5A.5.5 0 0 0 3 3.5Z"
      fill="currentColor"
    />
    <rect x="11.5" y="3" width="1.8" height="10" rx="0.9" fill="currentColor" />
  </svg>
);

/**
 * Строка порядка выступлений в окне звонка: кто говорит сейчас и кому готовиться.
 * Задача 21, CTO 07.09.2026.
 *
 * Повестка живёт сообщением в чате, и читает её клиент, а не окно звонка: у окна
 * нет прав на переписку, и давать их ради одной строки нельзя — оно увидело бы
 * всё, о чём люди пишут. Поэтому окно спрашивает клиент и просит его же передать
 * слово.
 */
export const AgendaStrip: FC = () => {
  const agenda = useAgenda();
  return agenda ? (
    <AgendaStripContent />
  ) : (
    <AgendaProvider>
      <AgendaStripContent />
    </AgendaProvider>
  );
};

const AgendaStripContent: FC = () => {
  const { t } = useTranslation();
  const { state, busy, error, act } = useAgenda()!;
  const reducedMotion = usePrefersReducedMotion();
  const elapsed = useElapsed(state);
  const secondsLeft =
    elapsed !== null && state?.limitSec ? state.limitSec - elapsed : null;

  if (!state || state.absent) return null;

  const speakingName = state.current;
  const nextName = state.next;
  // Последняя минута: строка мигает, чтобы это заметили, не вчитываясь
  // (CTO 14.09.2026). Тем, кому движение мешает, вместо мигания — рамка.
  const warning =
    state.speaking && secondsLeft !== null && secondsLeft <= WARN_SEC;
  const over = secondsLeft !== null && secondsLeft < 0;

  return (
    <div
      className={styles.strip}
      data-testid="agenda-strip"
      data-warning={warning && !reducedMotion ? "true" : undefined}
      data-warning-still={warning && reducedMotion ? "true" : undefined}
      data-over={over ? "true" : undefined}
    >
      {speakingName !== undefined ? (
        <span className={styles.now}>
          <span className={styles.label}>{t("agenda.now")}</span>
          <span
            className={styles.name}
            title={speakingName}
            data-speaking={
              state.speaking && !reducedMotion ? "true" : undefined
            }
            data-underline={
              state.speaking && reducedMotion ? "true" : undefined
            }
          >
            {speakingName}
          </span>
        </span>
      ) : (
        <span className={styles.label}>
          {state.finished ? t("agenda.finished") : t("agenda.not_started")}
        </span>
      )}

      {state.pausedAt && (
        <span className={styles.label}>{t("agenda.paused")}</span>
      )}
      {elapsed !== null && (
        <span className={styles.timer} data-testid="agenda-elapsed">
          {t("agenda.elapsed", { duration: formatLeft(elapsed) })}
        </span>
      )}
      {elapsed !== null && !state.limitSec && (
        <span className={styles.label}>{t("agenda.no_limit")}</span>
      )}
      {secondsLeft !== null && (
        <span className={styles.timer} data-testid="agenda-timer">
          {over
            ? t("agenda.over_time", { duration: formatLeft(secondsLeft) })
            : t("agenda.left", { duration: formatLeft(secondsLeft) })}
        </span>
      )}

      {nextName !== undefined && (
        <span className={styles.next}>
          <span className={styles.label}>{t("agenda.next_up")}</span>
          <span className={styles.name} title={nextName}>
            {nextName}
          </span>
        </span>
      )}

      {state.maySelect && state.entries && (
        <label className={styles.select} title={t("agenda.select_speaker")}>
          <select
            aria-label={t("agenda.select_speaker")}
            value={state.currentIndex ?? ""}
            disabled={busy}
            onChange={(e): void => {
              if (e.target.value !== "")
                void act(
                  "select",
                  Number(e.target.value),
                  state.entries?.find(
                    (entry) => entry.index === Number(e.target.value),
                  )?.slotId,
                );
            }}
          >
            <option value="" disabled>
              {t("agenda.select_speaker")}
            </option>
            {state.entries.map((entry) => (
              <option key={entry.index} value={entry.index}>
                {entry.index + 1}. {entry.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {error && <span role="alert">{t("agenda.update_failed")}</span>}
      {state.mayAct && !state.speaking && !state.finished && (
        <Button
          size="sm"
          kind="secondary"
          disabled={busy}
          onClick={(): void => void act("play")}
        >
          <PlaySolidIcon width={16} height={16} />
          {t(state.pausedAt ? "agenda.resume" : "agenda.play")}
        </Button>
      )}
      {state.mayAct && state.speaking && state.supportsPause && (
        <Button
          size="sm"
          kind="secondary"
          disabled={busy}
          onClick={(): void => void act("pause")}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 3h3v10H4zm5 0h3v10H9z" fill="currentColor" />
          </svg>
          {t("agenda.pause")}
        </Button>
      )}
      {state.mayAct && state.current !== undefined && (
        <Button
          size="sm"
          kind="secondary"
          disabled={busy}
          onClick={(): void => void act("next")}
        >
          <NextIcon />
          {t("agenda.next")}
        </Button>
      )}
    </div>
  );
};

export default AgendaStrip as FC<Record<string, never>> &
  (() => JSX.Element | null);
